import { useCallback } from 'react'
import { useTable, Column, Cell } from 'react-table'
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from 'react-beautiful-dnd'
import { CSSObject, Table, Tbody, Tr, Td } from '@ritualco/jutsu'
import { DragHandleIcon } from 'assets/Icons'
import { TableHeader } from './TableHeader'
import { MinData, RANK } from './utils'

/* Re-export */
export type { MinData }
export { RANK }

const renderCells = <D extends MinData>(
  cells: Cell<D>[],
  dragHandleId?: string
) => {
  return cells.map(cell => {
    const dragHandleStyles: CSSObject = {
      p: 0,
      py: 3,
      width: 6,
    }
    if (cell.column.id === dragHandleId) {
      return (
        <Td {...cell.getCellProps()} sx={dragHandleStyles}>
          <DragHandleIcon boxSize={6} />
        </Td>
      )
    }
    const isRank = cell.column.id === RANK
    const rankStyles: CSSObject = {
      color: 'grayscale.secondary',
      fontSize: 'xsmall',
      pl: 2,
      pr: 0,
      py: 3,
      textAlign: 'center',
      width: 8,
    }
    const defaultStyles: CSSObject = {
      fontSize: 'small',
      p: 3,
    }
    return (
      <Td {...cell.getCellProps()} sx={isRank ? rankStyles : defaultStyles}>
        {cell.render('Cell')}
      </Td>
    )
  })
}

const getDraggingStyles = (isDragging: boolean): CSSObject =>
  isDragging
    ? {
        backgroundColor: 'grayscale.greyFill',
        boxShadow: '0px 5px 12px rgba(0, 0, 0, 0.15)',
        /**
         * This is to avoid a display issue when dragging a row.
         * See https://github.com/atlassian/react-beautiful-dnd/blob/master/docs/patterns/tables.md#strategy-1-fixed-layouts
         */
        display: 'table',
      }
    : {}

export type OnReorder = (startIndex: number, endIndex: number) => void

export type DraggableTableProps<D extends MinData> = {
  /** Column config used by `react-table` */
  columns: Column<D>[]
  /** Data array used by `react-table` */
  data: Array<D>
  /** This function is called on every drag reorder action */
  onReorder: OnReorder
  /** Whether or not to display the table header. `false` by default. */
  showHeader?: boolean
  /** The column with this id will render as the drag handle */
  dragHandleId?: string
}

export const DraggableTable = <D extends MinData>({
  columns,
  data,
  onReorder,
  showHeader = false,
  dragHandleId,
}: DraggableTableProps<D>) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({ columns, data })

  const onDragEnd = useCallback(
    ({ source, destination }: DropResult) => {
      if (!destination) {
        return
      }
      if (destination.index === source.index) {
        return
      }
      onReorder(source.index, destination.index)
    },
    [onReorder]
  )

  return (
    <Table {...getTableProps()} sx={{ tableLayout: 'fixed' }}>
      <TableHeader
        showHeader={showHeader}
        dragHandleId={dragHandleId}
        headerGroups={headerGroups}
      />
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable">
          {provided => (
            <Tbody
              ref={provided.innerRef}
              {...provided.droppableProps}
              {...getTableBodyProps()}
            >
              {rows.map((row, index) => {
                prepareRow(row)
                return (
                  <Draggable
                    key={index}
                    draggableId={row.original.id}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <Tr
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        {...row.getRowProps()}
                        sx={getDraggingStyles(snapshot.isDragging)}
                      >
                        {renderCells(row.cells, dragHandleId)}
                      </Tr>
                    )}
                  </Draggable>
                )
              })}
              {provided.placeholder}
            </Tbody>
          )}
        </Droppable>
      </DragDropContext>
    </Table>
  )
}
